/** One member on the attendance roster: one big tap cycles present → absent → excused → (clear). */
import { Check, Circle, CircleCheckBig, CircleHelp, CircleMinus, CircleX, Clock, CloudOff, X, type LucideIcon } from 'lucide-react';
import type { AttendanceOverride, RosterEntryDTO } from '../../../shared/dto/events';
import { Avatar } from '../../components/people';
import { cx } from '../../components/ui';
import { useI18n, useT, type MessageKey } from '../../i18n';
import { formatTime } from '../../lib/dates';
import { OK_FILL, RSVP_KEY, WARN_FILL } from './shared';

export type RosterStatus = AttendanceOverride | null;

/** What the roster shows: the conductor's override, else "present" when the member checked in. */
export function rosterStatus(e: Pick<RosterEntryDTO, 'override' | 'checkedInAt'>): RosterStatus {
  return e.override ?? (e.checkedInAt != null ? 'present' : null);
}

/** Next override for a tap, cycling through what is displayed: present → absent → excused → clear. */
export function nextOverride(e: Pick<RosterEntryDTO, 'override' | 'checkedInAt'>): AttendanceOverride | null {
  switch (e.override) {
    case null:
      return e.checkedInAt != null ? 'absent' : 'present';
    case 'present':
      return 'absent';
    case 'absent':
      return 'excused';
    case 'excused':
      return null;
  }
}

export const STATUS_KEY: Record<'present' | 'absent' | 'excused' | 'unmarked', MessageKey> = {
  present: 'calendar.roster.present',
  absent: 'calendar.roster.absent',
  excused: 'calendar.roster.excused',
  unmarked: 'calendar.roster.unmarked',
};

const STATUS_STYLE: Record<'present' | 'absent' | 'excused' | 'unmarked', { icon: LucideIcon; pill: string; row: string }> = {
  present: {
    icon: CircleCheckBig,
    pill: `${OK_FILL} text-white`,
    row: 'border-[var(--ok)] bg-[color-mix(in_srgb,var(--ok)_10%,var(--surface))]',
  },
  absent: {
    icon: CircleX,
    pill: 'bg-[var(--danger)] text-white',
    row: 'border-[var(--danger)] bg-[color-mix(in_srgb,var(--danger)_8%,var(--surface))]',
  },
  excused: {
    icon: CircleMinus,
    pill: `${WARN_FILL} text-white`,
    row: 'border-[var(--warn)] bg-[color-mix(in_srgb,var(--warn)_10%,var(--surface))]',
  },
  unmarked: {
    icon: Circle,
    pill: 'border-2 border-dashed border-[var(--muted)] text-[var(--muted)]',
    row: 'border-[var(--line)] bg-[var(--surface)]',
  },
};

function RsvpMini({ e }: { e: RosterEntryDTO }) {
  const t = useT();
  const r = e.effectiveRsvp;
  const Icon = r === 'yes' ? Check : r === 'no' ? X : r === 'maybe' ? CircleHelp : Clock;
  // Text stays in --ink-2 / --muted / --accent for contrast; green/amber only colour the icon.
  return (
    <span className={cx('inline-flex items-center gap-1 font-semibold', r === 'no' ? 'text-[var(--muted)]' : r === null ? 'text-[var(--accent)]' : 'text-[var(--ink-2)]')}>
      <Icon className={cx('size-4', r === 'yes' ? 'text-[var(--ok-text)]' : r === 'maybe' ? 'text-[var(--warn-text)]' : '')} aria-hidden strokeWidth={2.5} />
      {r ? t(RSVP_KEY[r]) : t('calendar.rsvp.none')}
    </span>
  );
}

export function RosterRow({ e, onTap }: { e: RosterEntryDTO; onTap: () => void }) {
  const t = useT();
  const { loc } = useI18n();
  const status = rosterStatus(e) ?? 'unmarked';
  const s = STATUS_STYLE[status];
  return (
    <button
      type="button"
      onClick={onTap}
      aria-label={t('calendar.roster.mark_label', { name: e.fullName, state: t(STATUS_KEY[status]) })}
      className={cx('no-select flex min-h-[76px] w-full items-center gap-3 rounded-2xl border-2 p-3 text-left transition active:scale-[0.99]', s.row)}
    >
      <Avatar userId={e.userId} name={e.fullName} rev={e.avatarRev} section={e.section} size={48} />
      <div className="min-w-0 flex-1">
        <div className="flex min-w-0 items-center gap-2">
          <span className="truncate text-[18px] font-semibold">{e.fullName}</span>
          {e.status === 'leave' || e.status === 'inactive' || e.status === 'erased' ? (
            <span className="shrink-0 rounded-full bg-[var(--surface-2)] px-2 py-0.5 text-[12px] font-semibold text-[var(--muted)]">
              {e.status === 'leave' ? t('calendar.roster.on_leave') : t('calendar.roster.former')}
            </span>
          ) : null}
        </div>
        <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[14px] text-[var(--muted)]">
          <RsvpMini e={e} />
          {e.checkedInAt != null ? (
            <span className="inline-flex items-center gap-1">
              {t('calendar.roster.checked_in', { time: formatTime(e.checkedInAt, loc) })}
              {e.method === 'self-offline' ? (
                <span className="inline-flex items-center gap-0.5 rounded-full bg-[var(--surface-2)] px-1.5 text-[12px] font-semibold">
                  <CloudOff className="size-3" aria-hidden />
                  {t('calendar.roster.offline')}
                </span>
              ) : null}
            </span>
          ) : null}
        </div>
        {e.note ? <div className="truncate text-[14px] text-[var(--ink-2)] italic">{e.note}</div> : null}
      </div>
      <span className={cx('inline-flex shrink-0 items-center gap-1.5 rounded-full px-3 py-1.5 text-[14px] font-bold', s.pill)}>
        <s.icon className="size-5" aria-hidden />
        <span className="hidden min-[420px]:inline">{t(STATUS_KEY[status])}</span>
      </span>
    </button>
  );
}
